import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { notificationService } from '../../services/api';

// 初始狀態
const initialState = {
  notifications: [],
  selectedNotification: null,
  unreadCount: 0,
  loading: false,
  error: null,
  createSuccess: false, // 添加創建成功狀態
  createError: null // 添加創建錯誤狀態
};

// 異步操作：獲取通知
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (options = {}, { rejectWithValue }) => {
    try {
      const response = await notificationService.getNotifications(options);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '獲取通知失敗');
    }
  }
);

// 異步操作：獲取未讀通知數量
export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationService.getUnreadCount();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '獲取未讀通知數量失敗');
    }
  }
);

// 異步操作：獲取通知詳情
export const fetchNotificationById = createAsyncThunk(
  'notifications/fetchNotificationById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await notificationService.getNotificationById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '獲取通知詳情失敗');
    }
  }
);

// 異步操作：標記通知為已讀
export const markAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (id, { rejectWithValue }) => {
    try {
      const response = await notificationService.markAsRead(id);
      return { id, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '標記通知失敗');
    }
  }
);

// 異步操作：標記所有通知為已讀
export const markAllAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationService.markAllAsRead();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '標記所有通知失敗');
    }
  }
);

// 新增：創建通告
export const createAnnouncement = createAsyncThunk(
  'notifications/createAnnouncement',
  async (announcementData, { rejectWithValue }) => {
    try {
      const response = await notificationService.createAnnouncement(announcementData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '創建通告失敗');
    }
  }
);

// 通知切片
const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearSelectedNotification: (state) => {
      state.selectedNotification = null;
    },
    resetCreateStatus: (state) => {
      state.createSuccess = false;
      state.createError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // 獲取通知
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload.notifications || [];
        state.unreadCount = action.payload.unreadCount || 0;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 獲取未讀通知數量
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload.count || 0;
      })
      
      // 獲取通知詳情
      .addCase(fetchNotificationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotificationById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedNotification = action.payload;
      })
      .addCase(fetchNotificationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 標記通知為已讀
      .addCase(markAsRead.fulfilled, (state, action) => {
        const id = action.payload.id;
        const notification = state.notifications.find(n => n.id === id);
        if (notification) {
          notification.read = true;
        }
        if (state.selectedNotification && state.selectedNotification.id === id) {
          state.selectedNotification.read = true;
        }
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      })
      
      // 標記所有通知為已讀
      .addCase(markAllAsRead.fulfilled, (state) => {
        state.notifications.forEach(notification => {
          notification.read = true;
        });
        if (state.selectedNotification) {
          state.selectedNotification.read = true;
        }
        state.unreadCount = 0;
      })
      
      // 處理創建通告的狀態
      .addCase(createAnnouncement.pending, (state) => {
        state.loading = true;
        state.createSuccess = false;
        state.createError = null;
      })
      .addCase(createAnnouncement.fulfilled, (state, action) => {
        state.loading = false;
        state.createSuccess = true;
        // 如果響應包含創建的通告，將其添加到通知列表
        if (action.payload.notification) {
          state.notifications.unshift(action.payload.notification);
        }
      })
      .addCase(createAnnouncement.rejected, (state, action) => {
        state.loading = false;
        state.createError = action.payload;
      });
  }
});

export const { clearSelectedNotification, resetCreateStatus } = notificationSlice.actions;

export default notificationSlice.reducer; 